import { useCallback, useEffect, useState } from 'react';
import { Pencil, Users, Loader2 } from 'lucide-react';
import EnhancedListPickerModal from '../../Common/components/EnhancedListPickerModal';
import useUserOrg from '../../Authentication/components/hooks/useUserOrg';
import { useDeviceGroup } from './hooks/useDeviceGroup';
import useEnhancedDeviceGroupAssignment from './hooks/useEnhancedDeviceGroupAssignment';
import useFeatureFlags from '../../Features/components/hooks/useFeatureFlags';
import { FeatureFlags } from '../../Features/lib/constants';
import useOrganizations from '../../Organizations/hooks/useOrganizations';
import { Button } from '@/src/shadComponents/ui/button';
import { Badge } from '@/src/shadComponents/ui/badge';

interface EnhancedGalleryDeviceGroupAssignmentsProps {
    galleryId: number;
}

export default function EnhancedGalleryDeviceGroupAssignments({
    galleryId,
}: EnhancedGalleryDeviceGroupAssignmentsProps) {
    const { orgId } = useUserOrg();
    const { featureFlags } = useFeatureFlags();
    const { listOrganizations } = useOrganizations();

    const [devices, setDevices] = useState([]);
    const [assignments, setAssignments] = useState<any[]>([]);
    const [showPicker, setShowPicker] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [organizations, setOrganizations] = useState<
        { id: number; name: string }[]
    >([]);
    const [selectedOrgId, setSelectedOrgId] = useState(orgId);
    const [crossOrgSelections, setCrossOrgSelections] = useState<
        Record<number, number[]>
    >({});

    const isGlobalAdmin = featureFlags[FeatureFlags.GLOBAL_ADMIN_V1];
    const { getDeviceGroups } = useDeviceGroup(selectedOrgId);
    const { listDeviceAssignmentsForGallery, addDeviceAssignmentsForGallery } =
        useEnhancedDeviceGroupAssignment();

    useEffect(() => {
        if (isGlobalAdmin) {
            const loadOrganizations = async () => {
                try {
                    const orgList = await listOrganizations();
                    setOrganizations(orgList);
                } catch (error) {
                    console.error('Error loading organizations:', error);
                }
            };
            loadOrganizations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGlobalAdmin]);

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const assignments = await listDeviceAssignmentsForGallery(
                    galleryId,
                    selectedOrgId,
                );
                const devices = await getDeviceGroups();
                isMounted && setAssignments(assignments);
                isMounted && setDevices(devices);
            } catch (error) {
                console.error(error);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        load();
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrgId]);

    const saveAssignments = useCallback(
        async (
            newAssignments: number[],
            crossOrgSelections?: Record<number, number[]>,
        ) => {
            try {
                if (
                    crossOrgSelections &&
                    Object.keys(crossOrgSelections).length > 0
                ) {
                    const crossOrgDeviceGroups = Object.entries(
                        crossOrgSelections,
                    ).map(([orgIdStr, deviceGroups]) => ({
                        orgId: parseInt(orgIdStr),
                        deviceGroups,
                    }));
                    await addDeviceAssignmentsForGallery(
                        galleryId,
                        newAssignments,
                        crossOrgDeviceGroups,
                    );
                } else {
                    await addDeviceAssignmentsForGallery(
                        galleryId,
                        newAssignments,
                    );
                }

                const response = await listDeviceAssignmentsForGallery(
                    galleryId,
                    selectedOrgId,
                );
                setAssignments(response);
                setCrossOrgSelections({});
            } catch (error) {
                console.error(error);
            }
            setShowPicker(false);
        },
        [
            addDeviceAssignmentsForGallery,
            galleryId,
            listDeviceAssignmentsForGallery,
            selectedOrgId,
        ],
    );

    const handleOrgChange = useCallback((newOrgId: number) => {
        setSelectedOrgId(newOrgId);
        setDevices([]);
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-4">
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <Badge variant="secondary">
                    {assignments.length} group{assignments.length !== 1 ? 's' : ''} assigned
                </Badge>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPicker(true)}
                >
                    <Pencil className="size-3.5 mr-1.5" />
                    Edit
                </Button>
            </div>
            {assignments.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                    No device groups assigned to this gallery.
                </p>
            ) : (
                <ul className="divide-y divide-border rounded-md border">
                    {assignments.map((assignment: any) => (
                        <li
                            key={assignment.groupId}
                            className="flex items-center gap-3 px-4 py-3"
                        >
                            <Users className="size-4 text-muted-foreground shrink-0" />
                            <span className="text-sm font-medium">
                                {assignment.name}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            {showPicker && (
                <EnhancedListPickerModal
                    title="Select Device Groups"
                    list={devices}
                    selectedIds={assignments.map(({ groupId: id }: any) => id)}
                    onClose={() => setShowPicker(false)}
                    onSave={saveAssignments}
                    showOrgSelector={isGlobalAdmin}
                    organizations={organizations}
                    selectedOrgId={selectedOrgId}
                    onOrgChange={handleOrgChange}
                    crossOrgSelections={crossOrgSelections}
                    key={selectedOrgId}
                />
            )}
        </div>
    );
}
